/** Stream Player — shared types (FE-only IPTV UI). */

/** Legacy v1 shape (migration only). */
export type ServerType = 'xtream' | 'm3u_url' | 'm3u_file';

export interface XtreamCredentials {
  baseUrl: string;
  username: string;
  password?: string;
  rememberPassword: boolean;
}

export interface M3uUrlConfig {
  playlistUrl: string;
  epgUrl?: string;
  displayName: string;
}

export interface M3uFileConfig {
  displayName: string;
  content: string;
}

export interface SavedServer {
  id: string;
  type: ServerType;
  name: string;
  createdAt: number;
  xtream?: Omit<XtreamCredentials, 'password'> & { password?: string };
  m3uUrl?: M3uUrlConfig;
  m3uFile?: M3uFileConfig;
}

/** Current persisted server (v2). */
export type StreamSourceKind = 'xtream' | 'm3u_url' | 'm3u_file';

export interface StreamServer {
  id: string;
  /** User-defined label — shown in switcher and lists */
  name: string;
  server: string;
  username: string;
  password: string;
  createdAt: string;
  updatedAt: string;
  lastConnectedAt?: string;
  lastError?: string | null;
  /** Defaults to xtream for new entries; set for migrated playlists */
  sourceKind?: StreamSourceKind;
  m3uEpgUrl?: string;
  m3uFileContent?: string;
  /**
   * When true, before loading a library the app runs a **best-effort public-IP country hint**
   * (edge trace + IP APIs, confidence scoring, VPN/hosting heuristics) against `connectionCountryCode`.
   * Mismatches or unknown results **block** that connect attempt — never a verified real-world country.
   */
  restrictConnectionToCountry?: boolean;
  /** ISO 3166-1 alpha-2 (e.g. CA) — used when `restrictConnectionToCountry` is true. */
  connectionCountryCode?: string;
}

export type NavId = 'browse' | 'servers' | 'settings' | 'recents';

export type BrowseTab = 'live' | 'movies' | 'series' | 'favorites';

export interface CategoryItem {
  id: string;
  name: string;
}

export interface LiveChannel {
  id: string;
  categoryId: string;
  name: string;
  logo?: string;
  epgCurrent?: string;
  epgNext?: string;
  streamUrl?: string;
  streamId?: string;
  containerExtension?: string;
}

export interface VodItem {
  id: string;
  categoryId: string;
  name: string;
  poster?: string;
  year?: string;
  genre?: string;
  streamUrl?: string;
  containerExtension?: string;
}

export interface SeriesItem {
  id: string;
  categoryId: string;
  name: string;
  poster?: string;
  year?: string;
  genre?: string;
  streamUrl?: string;
}

export interface EpisodeItem {
  id: string;
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  streamUrl?: string;
  containerExtension?: string;
}

export interface SeriesDetail {
  series: SeriesItem;
  seasons: { seasonNumber: number; episodes: EpisodeItem[] }[];
}

export interface CatalogSnapshot {
  sourceId: string;
  liveCategories: CategoryItem[];
  liveChannels: LiveChannel[];
  vodCategories: CategoryItem[];
  vodItems: VodItem[];
  seriesCategories: CategoryItem[];
  seriesItems: SeriesItem[];
  seriesDetails: Record<string, SeriesDetail>;
}

export type PlayableKind = 'live' | 'vod' | 'episode';

export interface PlayableItem {
  id: string;
  kind: PlayableKind;
  title: string;
  streamUrl: string;
  poster?: string;
  isLive: boolean;
  seriesId?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  durationSeconds?: number;
}

export interface RecentEntry {
  key: string;
  refId: string;
  title: string;
  kind: PlayableKind;
  poster?: string;
  streamUrl: string;
  sourceId: string;
  playedAt: number;
  positionSeconds?: number;
  durationSeconds?: number;
}

export interface ContinueWatchingEntry {
  key: string;
  refId: string;
  title: string;
  poster?: string;
  streamUrl: string;
  sourceId: string;
  positionSeconds: number;
  durationSeconds: number;
  updatedAt: number;
}

export interface FavoriteEntry {
  key: string;
  title: string;
  kind: 'live' | 'vod' | 'series' | 'episode';
  poster?: string;
  sourceId: string;
  refId: string;
  seriesId?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  streamUrl?: string;
}

export interface PlayerSettings {
  viewMode: 'grid' | 'list';
  autoplay: boolean;
  /** `null` = no server selected (no library loaded). */
  activeServerId: string | null;
}

export const DEFAULT_PLAYER_SETTINGS: PlayerSettings = {
  viewMode: 'grid',
  autoplay: true,
  activeServerId: null,
};

export interface XtreamUserInfoResponse {
  user_info?: {
    username?: string;
    password?: string;
    auth?: number;
    status?: string;
    exp_date?: string;
  };
  server_info?: {
    url?: string;
    port?: string | number;
    https_port?: string | number;
    server_protocol?: string;
    timezone?: string;
  };
}

export interface XtreamCategoryRow {
  category_id?: string;
  category_name?: string;
  id?: string;
  name?: string;
}

export interface XtreamLiveStreamRow {
  stream_id?: number | string;
  name?: string;
  category_id?: string;
  stream_icon?: string;
  epg_channel_id?: string;
  /** e.g. ts, m3u8 — browser playback usually needs m3u8 (HLS), not raw .ts */
  container_extension?: string;
}

export interface XtreamVodStreamRow {
  stream_id?: number | string;
  name?: string;
  category_id?: string;
  stream_icon?: string;
  year?: string;
  genre?: string;
  container_extension?: string;
}

export interface XtreamSeriesRow {
  series_id?: number | string;
  name?: string;
  category_id?: string;
  cover?: string;
  year?: string;
  genre?: string;
}

export interface XtreamSeriesInfoResponse {
  seasons?: unknown[];
  episodes?: Record<string, XtreamEpisodeRow[]>;
  info?: { plot?: string; cast?: string };
}

export interface XtreamEpisodeRow {
  id?: number | string;
  title?: string;
  season?: number | string;
  episode_num?: number | string;
  container_extension?: string;
  direct_source?: string;
}
